import { z } from 'zod';
import { MoneyBagReportSchema, ReportIDSchema, ReportSchema } from './BusinessAnalysisSchemas.js';
import { ReportGenerationSourcesSchema } from './ReportSchemas.js';
import { ZodEntityReportSpecificationsSchema } from './ReportSpecificationSchemas.js';
/**
 * Schema for canonical report source - generates report using checked-in report specification
 */
export const CanonicalReportSourceSchema = z
    .object({
    type: z.literal('canonical'),
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "citrine")'),
    reportId: ReportIDSchema.describe('Report specification identifier (e.g., "fcf")'),
    startDate: z.iso.datetime().describe('Report period start date in ISO format (e.g., 2024-01-01T00:00:00Z)'),
    endDate: z.iso.datetime().describe('Report period end date in ISO format (e.g., 2024-12-31T23:59:59Z)'),
    periodType: z
        .enum(['MONTH', 'YEAR'])
        .optional()
        .describe('Period type for balance sheet comparison (defaults to MONTH)'),
    sources: ReportGenerationSourcesSchema.optional().describe('Optional ImportRef-keyed report generation source selection'),
})
    .refine(({ startDate, endDate }) => new Date(startDate) <= new Date(endDate), {
    message: 'startDate must be on or before endDate',
    path: ['endDate'],
});
/**
 * Schema for uploaded report source - uses a report provided directly
 */
export const UploadedReportSourceSchema = z.object({
    type: z.literal('uploaded'),
    report: ReportSchema.describe('The report data to use for comparison'),
});
/**
 * Schema for uploaded report source with MoneyBag values.
 * Used by report viewing endpoints that have migrated to multicurrency contracts.
 */
export const UploadedMoneyBagReportSourceSchema = z.object({
    type: z.literal('uploaded'),
    report: MoneyBagReportSchema.describe('The multicurrency report data to use for viewing'),
});
/**
 * Schema for report specification report source - generates report using custom report specifications.
 * The reportSpecifications field contains the transitive closure of all specs needed to render
 * the report (including any imported child entity specs).
 */
export const ReportSpecificationReportSourceSchema = z
    .object({
    type: z.literal('report-specification'),
    businessContext: z.string().min(1).describe('Business context identifier (e.g., "multiplier")'),
    reportId: ReportIDSchema.describe('Report specification identifier (e.g., "fcf")'),
    startDate: z.iso.datetime().describe('Report period start date in ISO format (e.g., 2024-01-01T00:00:00Z)'),
    endDate: z.iso.datetime().describe('Report period end date in ISO format (e.g., 2024-12-31T23:59:59Z)'),
    periodType: z
        .enum(['MONTH', 'YEAR'])
        .optional()
        .describe('Period type for balance sheet comparison (defaults to MONTH)'),
    sources: ReportGenerationSourcesSchema.optional().describe('Optional ImportRef-keyed report generation source selection'),
    reportSpecifications: ZodEntityReportSpecificationsSchema.describe('Map of all report specifications needed (transitive closure of imports)'),
})
    .refine(({ startDate, endDate }) => new Date(startDate) <= new Date(endDate), {
    message: 'startDate must be on or before endDate',
    path: ['endDate'],
});
/**
 * Discriminated union of all report source types.
 * Represents "where to get a report from" - used for both fetching a single report
 * and specifying sources for comparison.
 */
export const ReportSourceSchema = z.discriminatedUnion('type', [
    CanonicalReportSourceSchema,
    UploadedReportSourceSchema,
    ReportSpecificationReportSourceSchema,
]);
/**
 * Schema for report comparison request
 */
export const ReportComparisonRequestSchema = z.object({
    left: ReportSourceSchema.describe('Left side report source'),
    right: ReportSourceSchema.describe('Right side report source'),
});
/**
 * Discriminated union of report source types for multicurrency report viewing.
 * Mirrors ReportSourceSchema but uses MoneyBagReport for uploaded reports.
 */
export const MoneyBagReportSourceSchema = z.discriminatedUnion('type', [
    CanonicalReportSourceSchema,
    UploadedMoneyBagReportSourceSchema,
    ReportSpecificationReportSourceSchema,
]);
//# sourceMappingURL=ReportSourceSchemas.js.map